// Financial Reporting tab — the last stage of a request: generate the summary
// report, read and edit it, then file it into the loan's documents.
//
// It is a STAGE inside the request's own lifecycle drawer, not a drawer of its
// own. Generating a report is the third thing that happens to a request, after
// asking for it and reviewing what came back, and it only makes sense against
// the items sitting above it — which is exactly what a separate surface took
// away. The editor itself is the SAME component the /reports route renders,
// <ReportEditorBody>, extracted for that so the two can never drift apart.
//
// The report SUMMARISES the collected documents and REFERENCES them; it does
// not embed them. Nothing here downloads a merged pack, and nothing should.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileCheck2 } from "lucide-react";
import { Button, StatusBadge, useToast } from "@/design-system/components/ui";
import { FolderPicker } from "@/components/dms/FolderPicker";
import { ReportEditorBody } from "@/pages/reports/v32/ReportEditorBody";
import { extractErrorMessage } from "@/api/client";
import { formatDateInTz } from "@/design-system/lib/format";
import type { ReportRequestDto } from "@/api/portfolioAdmin/reportRequestsApi";
import {
  useFinaliseReportRequestReport,
  useGenerateReportRequestReport,
} from "@/hooks/portfolioAdmin/useReportRequests";

interface ReportRequestReportStageProps {
  loanId: string;
  request: ReportRequestDto;
  /** Reporting.ReviewRequests — the gate the generate/finalise endpoints carry. */
  canReview: boolean;
  tz?: string | null;
  /** Called once the report is filed, so the host can close or move on. */
  onFiled?: () => void;
}

/**
 * What the model is working through, in the order the server does it.
 *
 * 🔴 Not a percentage, and not a fake one. There is no progress signal on the
 * wire — the endpoint returns when it returns — so a bar would be inventing a
 * measurement. Naming the steps and counting real elapsed seconds says the two
 * things a waiting reviewer actually wants to know (it is doing something, and
 * roughly how long it has been) without claiming to know how far through it is.
 * The steps are the ones the summary is composed from, listed in
 * `ReportRequestReportService`.
 */
const GENERATION_STEPS = [
  "Reading the period and the figures the borrower reported",
  "Collecting your review notes and every document filed against the request",
  "Writing the summary",
];

/** Ticks once a second while active, so the elapsed label stays honest. */
function useElapsedSeconds(active: boolean): number {
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!active) {
      setStartedAt(null);
      return;
    }
    const started = Date.now();
    setStartedAt(started);
    setNow(started);
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);

  if (!active || startedAt === null) return 0;
  return Math.max(0, Math.floor((now - startedAt) / 1000));
}

export function ReportRequestReportStage({
  loanId,
  request,
  canReview,
  tz,
  onFiled,
}: ReportRequestReportStageProps) {
  const toast = useToast();
  const generate = useGenerateReportRequestReport(loanId);
  const finalise = useFinaliseReportRequestReport(loanId);

  const [folderId, setFolderId] = useState<string | null>(null);

  // Reset per request — a folder chosen against one request must never carry
  // over to the next one opened.
  useEffect(() => {
    setFolderId(null);
  }, [request.id]);

  const elapsed = useElapsedSeconds(generate.isPending);

  const filed = request.filedDocumentId !== null;
  const reportId = request.generatedReportId;

  const submitGenerate = () => {
    generate.mutate(request.id, {
      onSuccess: () => toast.success("Report generated."),
      onError: (err) => toast.error(extractErrorMessage(err)),
    });
  };

  const submitFinalise = async (flushDraft: () => Promise<void>) => {
    if (!folderId) return;
    try {
      // 🔴 The edits are persisted BEFORE the server is asked to render them.
      // The server reads the stored markdown, so skipping this files the
      // last-saved version and the reviewer's work disappears with no error.
      await flushDraft();
    } catch (err) {
      toast.error(extractErrorMessage(err));
      return;
    }
    finalise.mutate(
      { requestId: request.id, folderId },
      {
        onSuccess: () => {
          toast.success("Report filed into the loan's documents.");
          onFiled?.();
        },
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  };

  // --- Filed -----------------------------------------------------------------
  // 🔴 A filed request used to open a stage that said a report existed and then
  // did not show it — the one state where the reader most wants to read the
  // thing, answered with a sentence about where it went. The report row is
  // still there, so it is rendered.
  if (filed) {
    return (
      <div className="flex flex-col gap-space-4">
        <div
          className="rounded-md border border-border-light bg-white-warm p-space-4 flex flex-col gap-space-2"
          data-testid="report-already-filed"
        >
          <div className="flex items-center gap-space-2">
            <FileCheck2 size={16} aria-hidden className="text-status-success" />
            <StatusBadge tone="ok">Filed</StatusBadge>
          </div>
          <p className="text-small text-onyx m-0">
            This report was filed into the loan&apos;s documents
            {request.filedAt ? ` on ${formatDateInTz(request.filedAt, tz)}` : ""}. To
            report on this period again, issue a fresh request.
          </p>
          <p className="text-caption text-gray-mid m-0">
            The filed PDF lives on the{" "}
            <Link
              to={`/portfolio-admin/loans/${loanId}?tab=documents`}
              className="text-gold-shadow hover:underline underline-offset-4"
            >
              Documents tab
            </Link>
            . What follows is the report it was rendered from — editing it now
            changes the draft, <strong>not the filed PDF</strong>.
          </p>
        </div>

        {reportId ? (
          <div data-testid="report-filed-body">
            <ReportEditorBody
              reportId={reportId}
              showTitle={false}
              renderPrimaryAction={() => (
                <Link
                  to={`/reports/${reportId}`}
                  className="inline-flex items-center gap-space-1 text-caption text-gray-mid hover:text-gold-shadow whitespace-nowrap"
                >
                  Open in Reports
                  <ArrowRight size={14} aria-hidden />
                </Link>
              )}
            />
          </div>
        ) : (
          // Filed from a report row that has since gone. Say so rather than
          // rendering an editor over nothing.
          <p className="text-small text-gray-mid m-0" data-testid="report-filed-no-body">
            The draft this was rendered from is no longer available. The filed
            PDF on the Documents tab is the record.
          </p>
        )}
      </div>
    );
  }

  // --- Not generated yet -----------------------------------------------------
  if (!reportId) {
    return (
      <div className="flex flex-col gap-space-3" data-testid="report-not-generated">
        <p className="text-small text-onyx m-0">
          Phoenix writes a summary of everything collected against this request:
          the period, the figures the borrower reported, your review notes, and
          every document you filed — with a reference to where each one lives. It
          does not copy the documents into the report.
        </p>

        {request.completeWithExceptions && (
          <p
            className="text-small text-status-warning-ink m-0"
            data-testid="report-exceptions-warning"
          >
            This request was completed with exceptions, so the report will name
            what is still outstanding and read as incomplete.
          </p>
        )}

        {generate.isPending ? (
          <div
            className="rounded-md border border-border-light bg-white-warm p-space-3 flex flex-col gap-space-2"
            data-testid="report-generating"
            role="status"
            aria-live="polite"
          >
            <div className="flex items-center justify-between gap-space-3">
              <span className="text-small font-semibold text-onyx">
                Writing the report…
              </span>
              <span className="text-caption text-gray-mid tabular-nums">
                {elapsed}s elapsed
              </span>
            </div>
            <ol className="m-0 list-none p-0 flex flex-col gap-space-1">
              {GENERATION_STEPS.map((step) => (
                <li key={step} className="text-caption text-gray-mid">
                  · {step}
                </li>
              ))}
            </ol>
            <p className="text-caption text-gray-mid m-0">
              This normally takes under a minute. Leaving this drawer does not
              cancel it — the report appears here when it lands.
            </p>
          </div>
        ) : !canReview ? (
          <p className="text-small text-gray-mid m-0" data-testid="report-read-only">
            Generating the report needs the report-request reviewer permission.
          </p>
        ) : request.canGenerateReport ? (
          <div className="flex items-center justify-end">
            <Button variant="primary" onClick={submitGenerate} data-testid="report-generate">
              Generate report
            </Button>
          </div>
        ) : (
          <p className="text-small text-gray-mid m-0" data-testid="report-blocked">
            {request.outstandingCount} of {request.itemCount} item
            {request.itemCount === 1 ? "" : "s"}{" "}
            {request.outstandingCount === 1 ? "is" : "are"} still outstanding.
            Approve the rest, or mark the request complete with exceptions to
            generate the report anyway.
          </p>
        )}
      </div>
    );
  }

  // --- Generated, not yet filed ----------------------------------------------
  return (
    <div className="flex flex-col gap-space-4">
      <p className="text-caption text-gray-mid m-0">
        Read it, correct anything it got wrong, then file it. Filing renders what
        is in the editor — not what the model first wrote.
      </p>

      {/* 🔴 The destination comes BEFORE the editor, because it is a
          precondition of the button that sits inside it. The picker used to be
          below a full-height textarea while Finalise — rendered in the editor's
          own action slot, above — stayed disabled with no visible cause: the
          reader had to scroll past the whole report to find the control that
          was holding it, having been given no reason to look. */}
      {canReview && (
        <section
          className="rounded-md border border-border-light p-space-3 flex flex-col gap-space-2"
          data-testid="report-folder-section"
        >
          <h4 className="text-small font-semibold text-onyx m-0">File into folder</h4>
          <p className="text-caption text-gray-mid m-0">
            Choose the destination first — Finalise &amp; file stays disabled
            until you do.
          </p>
          <FolderPicker
            value={folderId}
            onChange={(id) => setFolderId(id)}
            treeSource={{ kind: "entity", entityType: "Loan", entityId: loanId }}
            search={{ placeholder: "Search every folder by name…" }}
            browseByDefault
          />
        </section>
      )}

      <ReportEditorBody
        reportId={reportId}
        showTitle={false}
        renderPrimaryAction={({ flushDraft, disabled }) =>
          canReview ? (
            <div className="flex flex-col items-end gap-space-1">
              <Button
                variant="primary"
                disabled={disabled || !folderId || finalise.isPending}
                loading={finalise.isPending}
                onClick={() => void submitFinalise(flushDraft)}
                data-testid="report-finalise"
              >
                Finalise &amp; file
              </Button>
              {!folderId && (
                <span className="text-caption text-gray-mid">
                  Choose a folder above first.
                </span>
              )}
            </div>
          ) : (
            <Link
              to={`/reports/${reportId}`}
              className="inline-flex items-center gap-space-1 text-caption text-gray-mid hover:text-gold-shadow whitespace-nowrap"
            >
              Open in Reports
              <ArrowRight size={14} aria-hidden />
            </Link>
          )
        }
      />
    </div>
  );
}
